import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ConversationChatConflictError,
  archiveConversationChat,
  createConversationChat,
  getConversationChat,
  getConversationShell,
  listConversationChats,
  renameConversationChat,
  restoreConversationChat
} from '@/api/conversation'
import type { ConversationChat, ConversationShell } from '@/api/conversation'
import { failureLine } from '@/common/errorMessages'

// Every surfaced workspace-shell failure falls back to this line when the
// thrown value carries no usable message.
const shellFailureFallback = 'The workspace navigation could not be updated. Try again.'

export type ConversationShellStatus = 'loading' | 'ready' | 'failure'

export type ConversationShellConflict = {
  projectId: number
  conversationId: number
  draftTitle: string
  current: ConversationChat
}

export type ConversationShellController = {
  shell?: ConversationShell
  status: ConversationShellStatus
  error?: string
  operationError?: string
  conflict?: ConversationShellConflict
  additionalChats: Record<number, Array<ConversationChat>>
  retry: () => void
  // Re-reads the rail without the loading flash, for chat facts the server changes
  // on its own account rather than through one of the mutations below.
  refreshChats: () => Promise<void>
  loadMoreWorkspaces: () => Promise<void>
  loadMoreChats: (projectId: number) => Promise<void>
  loadMoreArchived: () => Promise<void>
  createChat: (projectId: number) => Promise<ConversationChat | undefined>
  renameChat: (projectId: number, conversationId: number, title: string) => Promise<ConversationChat | undefined>
  archiveChat: (projectId: number, conversationId: number) => Promise<ConversationChat | undefined>
  restoreChat: (projectId: number, conversationId: number) => Promise<ConversationChat | undefined>
  clearConflict: () => void
}

type ShellScope = {
  orgId: number
  expandedProjectId?: number
}

function sameScope(left: ShellScope, right: ShellScope): boolean {
  return left.orgId === right.orgId && left.expandedProjectId === right.expandedProjectId
}

function mergeUniqueChats(
  existing: Array<ConversationChat>,
  incoming: Array<ConversationChat>
): Array<ConversationChat> {
  const byID = new Map(existing.map((chat) => [chat.conversationId, chat]))
  for (const chat of incoming) byID.set(chat.conversationId, chat)
  return [...byID.values()].sort(
    (left, right) => right.activityAtTsMs - left.activityAtTsMs || right.conversationId - left.conversationId
  )
}

export function useConversationShell(orgId: number, expandedProjectId?: number): ConversationShellController {
  const [loaded, setLoaded] = useState<{ orgId: number; shell: ConversationShell }>()
  const [status, setStatus] = useState<ConversationShellStatus>('loading')
  const [error, setError] = useState<string>()
  const [operationError, setOperationError] = useState<string>()
  const [conflict, setConflict] = useState<ConversationShellConflict>()
  const [additionalChats, setAdditionalChats] = useState<Record<number, Array<ConversationChat>>>({})
  const [generation, setGeneration] = useState(0)
  const requestRef = useRef<{ scope: ShellScope; sequence: number; controller: AbortController } | undefined>(undefined)
  const sequenceRef = useRef(0)
  const pendingCreateRef = useRef(new Map<string, string>())
  const shellRef = useRef(loaded)
  const scopeRef = useRef<ShellScope>({ orgId, ...(expandedProjectId === undefined ? {} : { expandedProjectId }) })
  shellRef.current = loaded
  scopeRef.current = { orgId, ...(expandedProjectId === undefined ? {} : { expandedProjectId }) }

  const fetchShell = useCallback(
    async (options: { preserve?: boolean } = {}) => {
      requestRef.current?.controller.abort()
      const scope = { orgId, ...(expandedProjectId === undefined ? {} : { expandedProjectId }) }
      const request = { scope, sequence: sequenceRef.current + 1, controller: new AbortController() }
      sequenceRef.current = request.sequence
      requestRef.current = request
      if (!options.preserve) setStatus('loading')
      setError(undefined)
      try {
        // The open workspace's archive comes back with the workspace itself. The rail's
        // archive row has to say whether there is anything behind it before anyone opens
        // it, and fetching that on the click could only answer after the click.
        const shell = await getConversationShell(orgId, {
          ...(expandedProjectId === undefined ? {} : { expandedProjectId, archivedProjectId: expandedProjectId }),
          signal: request.controller.signal
        })
        if (
          requestRef.current !== request ||
          request.controller.signal.aborted ||
          !sameScope(scopeRef.current, request.scope)
        ) {
          return undefined
        }
        setLoaded({ orgId, shell })
        setAdditionalChats({})
        setStatus('ready')
        return shell
      } catch (loadError) {
        if (
          requestRef.current !== request ||
          request.controller.signal.aborted ||
          !sameScope(scopeRef.current, request.scope)
        ) {
          return undefined
        }
        setError(failureLine(loadError, shellFailureFallback))
        setStatus('failure')
        return undefined
      }
    },
    [expandedProjectId, orgId]
  )

  useEffect(() => {
    setLoaded((current) => (current?.orgId === orgId ? current : undefined))
    setAdditionalChats({})
    setConflict(undefined)
    setOperationError(undefined)
    void fetchShell()
    return () => requestRef.current?.controller.abort()
  }, [fetchShell, generation, orgId])

  const refreshAfterMutation = useCallback(async () => {
    await fetchShell({ preserve: true })
  }, [fetchShell])

  const loadMoreWorkspaces = useCallback(async () => {
    const current = shellRef.current
    if (current?.orgId !== orgId || !current.shell.workspacesHasMore || current.shell.workspaceCursor === '') return
    const scope = scopeRef.current
    const cursor = current.shell.workspaceCursor
    setOperationError(undefined)
    try {
      const page = await getConversationShell(orgId, { before: cursor })
      if (shellRef.current !== current || !sameScope(scopeRef.current, scope)) return
      const seen = new Set(current.shell.workspaces.map((workspace) => workspace.projectId))
      const appended = page.workspaces.filter((workspace) => !seen.has(workspace.projectId))
      setLoaded({
        orgId,
        shell: {
          ...current.shell,
          workspaces: [...current.shell.workspaces, ...appended],
          workspaceCursor: page.workspaceCursor,
          workspacesHasMore: page.workspacesHasMore
        }
      })
    } catch (loadError) {
      setOperationError(failureLine(loadError, shellFailureFallback))
    }
  }, [orgId])

  const loadMoreChats = useCallback(async (projectId: number) => {
    const current = shellRef.current
    const workspace = current?.shell.workspaces.find((candidate) => candidate.projectId === projectId)
    if (current === undefined || workspace === undefined || !workspace.chats.hasMore || workspace.chats.cursor === '')
      return
    const scope = scopeRef.current
    const before = workspace.chats.cursor
    setOperationError(undefined)
    try {
      const page = await listConversationChats(projectId, { before, limit: 8 })
      if (shellRef.current !== current || !sameScope(scopeRef.current, scope)) return
      setAdditionalChats((pages) => ({ ...pages, [projectId]: mergeUniqueChats(pages[projectId] ?? [], page.items) }))
      setLoaded((latest) => {
        if (latest?.orgId !== current.orgId) return latest
        return {
          ...latest,
          shell: {
            ...latest.shell,
            workspaces: latest.shell.workspaces.map((candidate) =>
              candidate.projectId === projectId
                ? { ...candidate, chats: { ...candidate.chats, hasMore: page.hasMore, cursor: page.cursor } }
                : candidate
            )
          }
        }
      })
    } catch (loadError) {
      setOperationError(failureLine(loadError, shellFailureFallback))
    }
  }, [])

  const loadMoreArchived = useCallback(async () => {
    const current = shellRef.current
    const archived = current?.shell.archived
    if (current === undefined || archived === undefined || !archived.hasMore || archived.cursor === '') return
    const scope = scopeRef.current
    setOperationError(undefined)
    try {
      const page = await getConversationShell(orgId, {
        ...(expandedProjectId === undefined ? {} : { expandedProjectId }),
        archivedProjectId: archived.projectId,
        archivedBefore: archived.cursor
      })
      if (shellRef.current !== current || !sameScope(scopeRef.current, scope)) return
      const shellItems = archived.items
      const seen = new Set(shellItems.map((chat) => chat.conversationId))
      const archivedPage = page.archived
      if (archivedPage === undefined || archivedPage.projectId !== archived.projectId) return
      const appended = archivedPage.items.filter((chat) => !seen.has(chat.conversationId))
      setLoaded({
        orgId,
        shell: {
          ...current.shell,
          archived: {
            ...archived,
            items: [...shellItems, ...appended],
            cursor: archivedPage.cursor,
            hasMore: archivedPage.hasMore
          }
        }
      })
    } catch (loadError) {
      setOperationError(failureLine(loadError, shellFailureFallback))
    }
  }, [expandedProjectId, orgId])

  const createChat = useCallback(
    async (projectId: number) => {
      const scope = scopeRef.current
      setOperationError(undefined)
      const requestKey = `${scope.orgId}:${projectId}`
      const requestID = pendingCreateRef.current.get(requestKey) ?? crypto.randomUUID()
      pendingCreateRef.current.set(requestKey, requestID)
      try {
        const chat = await createConversationChat(projectId, requestID)
        if (!sameScope(scopeRef.current, scope)) return undefined
        pendingCreateRef.current.delete(requestKey)
        await refreshAfterMutation()
        return chat
      } catch (mutationError) {
        if (!sameScope(scopeRef.current, scope)) return undefined
        setOperationError(failureLine(mutationError, shellFailureFallback))
        return undefined
      }
    },
    [refreshAfterMutation]
  )

  const renameChat = useCallback(
    async (projectId: number, conversationId: number, title: string) => {
      const scope = scopeRef.current
      setOperationError(undefined)
      setConflict(undefined)
      try {
        const chat = await getConversationChat(projectId, conversationId)
        if (!sameScope(scopeRef.current, scope)) return undefined
        const renamed = await renameConversationChat(projectId, conversationId, {
          title,
          expectedRevisionTsUs: chat.revisionTsUs
        })
        if (!sameScope(scopeRef.current, scope)) return undefined
        await refreshAfterMutation()
        return renamed
      } catch (mutationError) {
        if (!sameScope(scopeRef.current, scope)) return undefined
        if (mutationError instanceof ConversationChatConflictError) {
          setConflict({ projectId, conversationId, draftTitle: title, current: mutationError.current })
        }
        setOperationError(failureLine(mutationError, shellFailureFallback))
        return undefined
      }
    },
    [refreshAfterMutation]
  )

  const mutateArchive = useCallback(
    async (command: 'archive' | 'restore', projectId: number, conversationId: number) => {
      const scope = scopeRef.current
      setOperationError(undefined)
      try {
        const chat = await getConversationChat(projectId, conversationId)
        if (!sameScope(scopeRef.current, scope)) return undefined
        const updated = await (command === 'archive' ? archiveConversationChat : restoreConversationChat)(
          projectId,
          conversationId,
          chat.revisionTsUs
        )
        if (!sameScope(scopeRef.current, scope)) return undefined
        await refreshAfterMutation()
        return updated
      } catch (mutationError) {
        if (!sameScope(scopeRef.current, scope)) return undefined
        setOperationError(failureLine(mutationError, shellFailureFallback))
        return undefined
      }
    },
    [refreshAfterMutation]
  )

  const shell = loaded?.orgId === orgId ? loaded.shell : undefined
  return {
    ...(shell === undefined ? {} : { shell }),
    status: shell === undefined && status === 'ready' ? 'loading' : status,
    ...(error === undefined ? {} : { error }),
    ...(operationError === undefined ? {} : { operationError }),
    ...(conflict === undefined ? {} : { conflict }),
    additionalChats,
    retry: () => setGeneration((current) => current + 1),
    refreshChats: refreshAfterMutation,
    loadMoreWorkspaces,
    loadMoreChats,
    loadMoreArchived,
    createChat,
    renameChat,
    archiveChat: (projectId, conversationId) => mutateArchive('archive', projectId, conversationId),
    restoreChat: (projectId, conversationId) => mutateArchive('restore', projectId, conversationId),
    clearConflict: () => setConflict(undefined)
  }
}
